import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { formatDecimal } from '../../utils/format';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { UnitInput } from '../../components/ui/unit-input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { CharCounter, AtLimit } from '../../components/ui/char-counter';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { ProjectFormData } from '../../types';
import {
    FunctionalUnit,
    CreateFunctionalUnitDTO,
    functionalUnitService
} from '../../services/functionalUnitService';
import { getAllUFTypes, UFType } from '../../services/ufTypeService';
import { getAllUFLocations, UFLocation } from '../../services/ufLocationService';
import {
    Trash2,
    Plus,
    Edit,
    Search,
    AlertCircle,
    ArrowLeft,
    LayoutGrid,
    Ruler,
    FileText,
    CheckCircle2,
    Tag,
    ChevronLeft,
    ChevronRight,
    Copy
} from 'lucide-react';
import projectService from '../../services/projectService';
import LoadingScreen from '../../components/ui/LoadingScreen';
import { showSuccess } from '../../components/Alerts';
import { handleApiError, handleLoadError, handleDeleteError } from '../../utils/errorHandler';
import { getColorClasses, DEFAULT_TYPE_COLOR, DEFAULT_LOCATION_COLOR } from '../../components/FunctionalUnits/labelColors';
import NotesModal from '../../components/ui/NotesModal';
import { PERMISSIONS } from '../../constants/permissions';
import RequirePermission from '../../components/RequirePermission';

const ROWS_PER_PAGE = 12;

const ProjectFunctionalUnits: React.FC = () => {
    const { projectId } = useParams<{ projectId: string }>();
    const navigate = useNavigate();

    const [functionalUnits, setFunctionalUnits] = useState<FunctionalUnit[]>([]);
    const [currentProject, setCurrentProject] = useState<ProjectFormData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingUnit, setEditingUnit] = useState<FunctionalUnit | null>(null);
    const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState<number | 'ALL'>('ALL');
    const [filterLocation, setFilterLocation] = useState<number | 'ALL'>('ALL');
    const [page, setPage] = useState(1);

    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [unitToDelete, setUnitToDelete] = useState<FunctionalUnit | null>(null);
    const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
    const [selectedUnitNotes, setSelectedUnitNotes] = useState<{ floor: string; letter: string; notes: string } | null>(null);

    // Estados para tipos y ubicaciones disponibles
    const [availableTypes, setAvailableTypes] = useState<UFType[]>([]);
    const [availableLocations, setAvailableLocations] = useState<UFLocation[]>([]);

    const [formData, setFormData] = useState<CreateFunctionalUnitDTO>({
        floor: '',
        letter: '',
        coveredArea: 0,
        semiCoveredArea: 0,
        storageArea: 0,
        freeArea: 0,
        notes: '',
        ufTypeId: null,
        ufLocationId: null
    });

    const loadData = useCallback(async () => {
        if (!projectId) return;

        try {
            setIsLoading(true);

            // Load project details
            const projectData = await projectService.getById(Number(projectId));
            setCurrentProject(projectData);

            // Load available types and locations
            const [types, locations] = await Promise.all([
                getAllUFTypes(),
                getAllUFLocations()
            ]);
            setAvailableTypes(types);
            setAvailableLocations(locations);

            // Load functional units
            const units = await functionalUnitService.getAllFunctionalUnits(Number(projectId));
            setFunctionalUnits(units);
        } catch (error) {
            handleLoadError(error, 'los datos del proyecto');
        } finally {
            setIsLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleInputChange = (field: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        setFormErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[field];
            if (['coveredArea', 'semiCoveredArea', 'storageArea', 'freeArea'].includes(field)) {
                delete newErrors.totalSurface;
            }
            return newErrors;
        });
    };

    const calculateTotalSurface = () => {
        const covered = formData?.coveredArea || 0;
        const semiCovered = formData?.semiCoveredArea || 0;
        const storage = formData?.storageArea || 0;
        const free = formData?.freeArea || 0;
        return covered + semiCovered + storage + free;
    };

    const validateForm = (): boolean => {
        const errors: { [key: string]: string } = {};

        if (!formData.floor.trim()) {
            errors.floor = 'El piso es obligatorio';
        }

        if (!formData.letter.trim()) {
            errors.letter = 'La letra es obligatoria';
        }

        if (formData.floor.trim() && formData.letter.trim()) {
            const duplicate = functionalUnits.find(u =>
                u.floor.toLowerCase() === formData.floor.trim().toLowerCase() &&
                u.letter.toLowerCase() === formData.letter.trim().toLowerCase() &&
                (!editingUnit || u.id !== editingUnit.id)
            );
            if (duplicate) {
                errors.letter = `Ya existe una unidad con piso "${formData.floor}" y letra "${formData.letter}" en este proyecto`;
            }
        }

        if (formData.coveredArea < 0) {
            errors.coveredArea = 'La superficie cubierta debe ser mayor o igual a 0';
        }

        if (formData.semiCoveredArea < 0) {
            errors.semiCoveredArea = 'La superficie semi-cubierta debe ser mayor o igual a 0';
        }

        if (formData.storageArea < 0) {
            errors.storageArea = 'La superficie de baulera debe ser mayor o igual a 0';
        }

        if (formData.freeArea < 0) {
            errors.freeArea = 'La superficie libre debe ser mayor o igual a 0';
        }

        if (formData.floor.length > 50) {
            errors.floor = 'El piso no puede superar los 50 caracteres';
        }

        if (formData.letter.length > 50) {
            errors.letter = 'La letra/unidad no puede superar los 50 caracteres';
        }

        if ((formData.notes || '').length > 255) {
            errors.notes = 'Las notas no pueden superar los 255 caracteres';
        }

        const totalSurface = calculateTotalSurface();
        if (totalSurface <= 0) {
            errors.totalSurface = 'La superficie total debe ser mayor a 0';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) return;
        if (!projectId) return;

        try {
            const unitData: CreateFunctionalUnitDTO = {
                floor: formData.floor,
                letter: formData.letter,
                coveredArea: formData.coveredArea,
                semiCoveredArea: formData.semiCoveredArea,
                storageArea: formData.storageArea,
                freeArea: formData.freeArea,
                notes: formData.notes || '',
                ufTypeId: formData.ufTypeId,
                ufLocationId: formData.ufLocationId
            };

            if (editingUnit) {
                await functionalUnitService.updateFunctionalUnit(editingUnit.id, {
                    floor: formData.floor,
                    letter: formData.letter,
                    coveredArea: formData.coveredArea,
                    semiCoveredArea: formData.semiCoveredArea,
                    storageArea: formData.storageArea,
                    freeArea: formData.freeArea,
                    notes: formData.notes || '',
                    ufTypeId: formData.ufTypeId,
                    ufLocationId: formData.ufLocationId
                });
                showSuccess('Unidad funcional actualizada exitosamente');
            } else {
                await functionalUnitService.createFunctionalUnit(Number(projectId), unitData);
                showSuccess('Unidad funcional creada exitosamente');
            }

            setIsDialogOpen(false);
            setEditingUnit(null);
            setFormErrors({});
            // Reset form
            setFormData({
                floor: '',
                letter: '',
                coveredArea: 0,
                semiCoveredArea: 0,
                storageArea: 0,
                freeArea: 0,
                notes: '',
                ufTypeId: null,
                ufLocationId: null
            });

            loadData();
        } catch (error) {
            handleApiError(
                error,
                `Error al ${editingUnit ? 'actualizar' : 'guardar'} la unidad funcional`
            );
        }
    };

    const handleEdit = (unit: FunctionalUnit) => {
        setEditingUnit(unit);
        setFormData({
            floor: unit.floor,
            letter: unit.letter,
            coveredArea: unit.coveredArea,
            semiCoveredArea: unit.semiCoveredArea,
            storageArea: unit.storageArea,
            freeArea: unit.freeArea,
            notes: unit.notes || '',
            ufTypeId: unit.ufType?.id || null,
            ufLocationId: unit.ufLocation?.id || null
        });
        setIsDialogOpen(true);
    };

    const handleDelete = async (unit: FunctionalUnit) => {
        setUnitToDelete(unit);
        setIsDeleteDialogOpen(true);
    };

    const handleViewNotes = (unit: FunctionalUnit) => {
        setSelectedUnitNotes({
            floor: unit.floor,
            letter: unit.letter,
            notes: unit.notes || ''
        });
        setIsNotesModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!unitToDelete) return;

        try {
            await functionalUnitService.deleteFunctionalUnit(unitToDelete.id);
            showSuccess('Unidad funcional eliminada exitosamente');
            setFunctionalUnits(prev => prev.filter(unit => unit.id !== unitToDelete.id));
        } catch (error) {
            handleDeleteError(error, 'la unidad funcional');
        } finally {
            setIsDeleteDialogOpen(false);
            setUnitToDelete(null);
        }
    };

    const handleAddNew = () => {
        setEditingUnit(null);
        setFormErrors({});
        setFormData({
            floor: '',
            letter: '',
            coveredArea: 0,
            semiCoveredArea: 0,
            storageArea: 0,
            freeArea: 0,
            notes: '',
            ufTypeId: null,
            ufLocationId: null
        });
        setIsDialogOpen(true);
    };

    const handleDuplicate = (unit: FunctionalUnit) => {
        setEditingUnit(null);
        setFormErrors({});
        setFormData({
            floor: '',
            letter: '',
            coveredArea: unit.coveredArea,
            semiCoveredArea: unit.semiCoveredArea,
            storageArea: unit.storageArea,
            freeArea: unit.freeArea,
            notes: unit.notes || '',
            ufTypeId: unit.ufType?.id || null,
            ufLocationId: unit.ufLocation?.id || null
        });
        setIsDialogOpen(true);
    };

    const applyFilters = () => {
        return functionalUnits
            .filter(unit => {
                const term = searchTerm.toLowerCase();
                const matchesSearch = searchTerm === '' ||
                    unit.floor.toLowerCase().includes(term) ||
                    unit.letter.toLowerCase().includes(term) ||
                    (unit.ufType?.name || '').toLowerCase().includes(term) ||
                    (unit.ufLocation?.name || '').toLowerCase().includes(term);

                const matchesType = filterType === 'ALL' || unit.ufType?.id === filterType;
                const matchesLocation = filterLocation === 'ALL' || unit.ufLocation?.id === filterLocation;

                return matchesSearch && matchesType && matchesLocation;
            })
            .sort((a, b) => {
                const aFloorIsNum = /^\d/.test(a.floor);
                const bFloorIsNum = /^\d/.test(b.floor);
                if (!aFloorIsNum && bFloorIsNum) return -1;
                if (aFloorIsNum && !bFloorIsNum) return 1;
                const floorCmp = a.floor.localeCompare(b.floor, undefined, { numeric: true, sensitivity: 'base' });
                if (floorCmp !== 0) return floorCmp;
                return a.letter.localeCompare(b.letter, undefined, { numeric: true, sensitivity: 'base' });
            });
    };

    const filteredAndSortedUnits = applyFilters();
    const totalPages = Math.ceil(filteredAndSortedUnits.length / ROWS_PER_PAGE);
    const safePage = Math.min(page, Math.max(1, totalPages));
    const pageUnits = filteredAndSortedUnits.slice((safePage - 1) * ROWS_PER_PAGE, safePage * ROWS_PER_PAGE);

    useEffect(() => { setPage(1); }, [searchTerm, filterType, filterLocation]);

    if (isLoading) {
        return <LoadingScreen message="Cargando unidades funcionales..." />;
    }

    return (
        <div className="w-full h-full p-4 space-y-6 sm:p-6">
            {/* Card Container */}
            <div className="overflow-hidden border rounded-lg shadow-sm bg-card border-border">
                {/* Header */}
                <div className="px-4 py-5 border-b sm:px-6 border-border">
                    <div className="flex flex-col items-start justify-between gap-4 mb-6 sm:flex-row sm:items-center">
                        <div className="flex items-center gap-4">
                            <Button
                                variant="outline"
                                onClick={() => navigate('/functional-units')}
                                className="flex items-center gap-2 rounded-lg"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                Volver
                            </Button>
                            <div>
                                <h1 className="text-2xl font-bold text-foreground">
                                    {currentProject?.name || 'Unidades Funcionales'}
                                </h1>
                                <p className="mt-1 text-sm text-muted-foreground">
                                    {currentProject?.client || 'Administra las unidades del proyecto'}
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-2">
                            <RequirePermission permission={PERMISSIONS.UNIDADES.EDITAR}>
                                <Button
                                    size="sm"
                                    onClick={handleAddNew}
                                    className="flex items-center gap-2 rounded-lg bg-primary text-primary-foreground hover:opacity-90"
                                >
                                    <Plus className="w-4 h-4" />
                                    Nueva Unidad
                                </Button>
                            </RequirePermission>
                        </div>
                    </div>

                    {/* Search and Filters */}
                    <div className="flex flex-col items-end gap-4 md:flex-row">
                        {/* Filters */}
                        <div className="flex flex-wrap gap-4">
                            <div className="space-y-2">
                                <label className="text-xs font-medium uppercase text-muted-foreground">
                                    Tipo de Unidad
                                </label>
                                <Select value={String(filterType)} onValueChange={(value) => setFilterType(value === 'ALL' ? 'ALL' : Number(value))}>
                                    <SelectTrigger className="w-[180px] rounded-lg">
                                        <SelectValue placeholder="Todos los tipos" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="ALL">Todos los tipos</SelectItem>
                                        {availableTypes.map((type) => (
                                            <SelectItem key={type.id} value={String(type.id)}>
                                                {type.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-medium uppercase text-muted-foreground">
                                    Ubicación
                                </label>
                                <Select value={String(filterLocation)} onValueChange={(value) => setFilterLocation(value === 'ALL' ? 'ALL' : Number(value))}>
                                    <SelectTrigger className="w-[180px] rounded-lg">
                                        <SelectValue placeholder="Todas las ubicaciones" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="ALL">Todas las ubicaciones</SelectItem>
                                        {availableLocations.map((location) => (
                                            <SelectItem key={location.id} value={String(location.id)}>
                                                {location.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* Search */}
                        <div className="w-full space-y-2 md:w-72 md:ml-auto">
                            <label className="text-xs font-medium uppercase text-muted-foreground">
                                Buscar
                            </label>
                            <div className="relative">
                                <Search className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2 text-muted-foreground" />
                                <Input
                                    placeholder="Buscar por piso, letra, tipo, ubicación..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="rounded-lg pl-9 focus-visible:ring-0 focus-visible:ring-offset-0"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Units Grid */}
                <div className="p-6">
                    {filteredAndSortedUnits.length === 0 ? (
                        <div className="py-12 text-center">
                            <LayoutGrid className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                            <h3 className="mb-2 text-lg font-medium text-foreground">No hay unidades funcionales</h3>
                            <p className="mb-4 text-sm text-muted-foreground">Comienza agregando unidades al proyecto</p>
                            <RequirePermission permission={PERMISSIONS.UNIDADES.EDITAR}>
                                <Button
                                    onClick={handleAddNew}
                                    className="bg-primary text-primary-foreground hover:opacity-90"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Nueva Unidad
                                </Button>
                            </RequirePermission>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                                {pageUnits.map((unit) => {
                                    const totalArea = unit.coveredArea + unit.semiCoveredArea + unit.storageArea + unit.freeArea;
                                    const surfaces = [
                                        { label: 'Cubierta', value: unit.coveredArea },
                                        { label: 'Semi-cub.', value: unit.semiCoveredArea },
                                        { label: 'Baulera', value: unit.storageArea },
                                        { label: 'Libre', value: unit.freeArea },
                                    ].filter(s => s.value > 0);

                                    return (
                                        <div
                                            key={unit.id}
                                            className="flex flex-col bg-card border border-border rounded-xl overflow-hidden transition-shadow hover:shadow-md"
                                        >
                                            {/* Card Header */}
                                            <div className="flex items-start justify-between px-4 pt-4 pb-3">
                                                <div>
                                                    <p className="text-base font-semibold text-foreground leading-tight">
                                                        Piso {unit.floor}
                                                    </p>
                                                    <p className="text-sm text-muted-foreground">
                                                        Unidad {unit.letter}
                                                    </p>
                                                </div>
                                                {unit.isSale ? (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-destructive/10 text-destructive border border-destructive/20">
                                                        <Tag className="w-3 h-3" />
                                                        Vendida
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800/30">
                                                        <CheckCircle2 className="w-3 h-3" />
                                                        Disponible
                                                    </span>
                                                )}
                                            </div>

                                            {/* Divider */}
                                            <div className="mx-4 border-t border-border" />

                                            {/* Badges: Tipo + Ubicación */}
                                            <div className="px-4 py-3 flex flex-wrap gap-1.5">
                                                {unit.ufType ? (
                                                    <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold ${getColorClasses(unit.ufType.icon ?? DEFAULT_TYPE_COLOR).bg} ${getColorClasses(unit.ufType.icon ?? DEFAULT_TYPE_COLOR).text}`}>
                                                        {unit.ufType.name}
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-muted text-muted-foreground">
                                                        Sin tipo
                                                    </span>
                                                )}
                                                {unit.ufLocation ? (
                                                    <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold ${getColorClasses(unit.ufLocation.color ?? DEFAULT_LOCATION_COLOR).bg} ${getColorClasses(unit.ufLocation.color ?? DEFAULT_LOCATION_COLOR).text}`}>
                                                        {unit.ufLocation.name}
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-muted text-muted-foreground">
                                                        Sin ubicación
                                                    </span>
                                                )}
                                            </div>

                                            {/* Surfaces */}
                                            {surfaces.length > 0 && (
                                                <>
                                                    <div className="mx-4 border-t border-border" />
                                                    <div className="px-4 py-3 grid grid-cols-2 gap-x-4 gap-y-1">
                                                        {surfaces.map(s => (
                                                            <div key={s.label} className="flex items-center justify-between gap-1">
                                                                <span className="text-xs text-muted-foreground">{s.label}</span>
                                                                <span className="text-xs font-medium text-foreground">{formatDecimal(s.value)} m²</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </>
                                            )}

                                            {/* Total + Actions */}
                                            <div className="mt-auto mx-4 border-t border-border" />
                                            <div className="flex items-center justify-between px-4 py-3">
                                                <div className="flex items-center gap-1.5">
                                                    <Ruler className="w-3.5 h-3.5 text-muted-foreground" />
                                                    <span className="text-sm font-bold text-foreground">{formatDecimal(totalArea)} m²</span>
                                                </div>
                                                <div className="flex items-center gap-0.5">
                                                    <button
                                                        onClick={() => handleViewNotes(unit)}
                                                        className="inline-flex items-center justify-center p-1.5 rounded-lg transition-colors hover:bg-accent text-muted-foreground hover:text-foreground"
                                                        title="Ver notas"
                                                    >
                                                        <FileText className="w-4 h-4" />
                                                    </button>
                                                    <RequirePermission permission={PERMISSIONS.UNIDADES.EDITAR}>
                                                        <button
                                                            onClick={() => handleDuplicate(unit)}
                                                            className="inline-flex items-center justify-center p-1.5 rounded-lg transition-colors hover:bg-accent text-muted-foreground hover:text-foreground"
                                                            title="Duplicar unidad"
                                                        >
                                                            <Copy className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleEdit(unit)}
                                                            className="inline-flex items-center justify-center p-1.5 rounded-lg transition-colors hover:bg-accent text-muted-foreground hover:text-foreground"
                                                            title="Editar unidad"
                                                        >
                                                            <Edit className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(unit)}
                                                            className="inline-flex items-center justify-center p-1.5 rounded-lg transition-colors hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                                                            title="Eliminar unidad"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </RequirePermission>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Footer */}
                            <div className="flex items-center justify-between pt-4 mt-2 border-t border-border">
                                <p className="text-sm text-muted-foreground">
                                    Mostrando{' '}
                                    <span className="font-semibold text-foreground">{pageUnits.length}</span> de{' '}
                                    <span className="font-semibold text-foreground">{filteredAndSortedUnits.length}</span>{' '}
                                    unidad{filteredAndSortedUnits.length !== 1 ? 'es' : ''}
                                </p>
                                {totalPages > 1 && (
                                    <div className="flex items-center gap-2">
                                        <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={safePage === 1}>
                                            <ChevronLeft className="w-4 h-4" />
                                        </Button>
                                        <span className="text-sm text-foreground">{safePage} / {totalPages}</span>
                                        <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={safePage === totalPages}>
                                            <ChevronRight className="w-4 h-4" />
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div >

            {/* Add/Edit Dialog */}
            < Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen} >
                <DialogContent className="max-w-2xl bg-card border-border">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-3 text-xl font-bold text-foreground">
                            {editingUnit ? <Edit className="w-5 h-5 text-indigo-600" /> : <Plus className="w-5 h-5 text-indigo-600" />}
                            {editingUnit ? 'Editar Unidad' : 'Nueva Unidad Funcional'}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="grid gap-6 py-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="floor" className="text-foreground">Piso <span className="text-red-500">*</span></Label>
                                <Input
                                    id="floor"
                                    value={formData.floor}
                                    onChange={(e) => handleInputChange('floor', e.target.value)}
                                    maxLength={50}
                                    className={`bg-background border-input ${formErrors.floor ? 'border-destructive' : ''}`}
                                    placeholder="Ej: 1"
                                />
                                <AtLimit value={formData.floor} max={50} />
                                {formErrors.floor && <p className="text-xs text-destructive">{formErrors.floor}</p>}
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="letter" className="text-foreground">Letra / Unidad <span className="text-red-500">*</span></Label>
                                <Input
                                    id="letter"
                                    value={formData.letter}
                                    onChange={(e) => handleInputChange('letter', e.target.value)}
                                    maxLength={50}
                                    className={`bg-background border-input ${formErrors.letter ? 'border-destructive' : ''}`}
                                    placeholder="Ej: A"
                                />
                                <AtLimit value={formData.letter} max={50} />
                                {formErrors.letter && <p className="text-xs text-destructive">{formErrors.letter}</p>}
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-foreground">Tipo de Unidad</Label>
                                <Select
                                    value={formData.ufTypeId === null ? 'null' : String(formData.ufTypeId)}
                                    onValueChange={(value) => handleInputChange('ufTypeId', value === 'null' ? null : Number(value))}
                                    disabled={availableTypes.length === 0}
                                >
                                    <SelectTrigger className="bg-background border-input text-foreground">
                                        <SelectValue placeholder="Selecciona un tipo" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-popover border-border">
                                        <SelectItem value="null">Sin definir</SelectItem>
                                        {availableTypes.map((type) => (
                                            <SelectItem key={type.id} value={String(type.id)}>
                                                {type.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {availableTypes.length === 0 && (
                                    <p className="text-xs text-destructive">No hay tipos de UF disponibles. Crea al menos uno en la pestaña "Tipos de UF".</p>
                                )}
                            </div>
                            <div className="space-y-2">
                                <Label className="text-foreground">Ubicación</Label>
                                <Select
                                    value={formData.ufLocationId === null ? 'null' : String(formData.ufLocationId)}
                                    onValueChange={(value) => handleInputChange('ufLocationId', value === 'null' ? null : Number(value))}
                                    disabled={availableLocations.length === 0}
                                >
                                    <SelectTrigger className="bg-background border-input text-foreground">
                                        <SelectValue placeholder="Selecciona una ubicación" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-popover border-border">
                                        <SelectItem value="null">Sin definir</SelectItem>
                                        {availableLocations.map((location) => (
                                            <SelectItem key={location.id} value={String(location.id)}>
                                                {location.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {availableLocations.length === 0 && (
                                    <p className="text-xs text-destructive">No hay ubicaciones de UF disponibles. Crea al menos una en la pestaña "Ubicaciones de UF".</p>
                                )}
                            </div>
                        </div>

                        <div className="space-y-4">
                            <h4 className="pb-2 font-medium border-b text-foreground border-border">Superficies (m²)</h4>
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <div className="space-y-2">
                                    <Label htmlFor="coveredArea" className="text-xs text-muted-foreground">Cubierta</Label>
                                    <UnitInput
                                        id="coveredArea"
                                        unit="m²"
                                        value={formData.coveredArea || undefined}
                                        onValueChange={(value) => handleInputChange('coveredArea', value || 0)}
                                        placeholder="0,00"
                                        className={formErrors.coveredArea ? 'border-destructive' : ''}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="semiCoveredArea" className="text-xs text-muted-foreground">Semi-cubierta</Label>
                                    <UnitInput
                                        id="semiCoveredArea"
                                        unit="m²"
                                        value={formData.semiCoveredArea || undefined}
                                        onValueChange={(value) => handleInputChange('semiCoveredArea', value || 0)}
                                        placeholder="0,00"
                                        className={formErrors.semiCoveredArea ? 'border-destructive' : ''}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="storageArea" className="text-xs text-muted-foreground">Baulera/Otros</Label>
                                    <UnitInput
                                        id="storageArea"
                                        unit="m²"
                                        value={formData.storageArea || undefined}
                                        onValueChange={(value) => handleInputChange('storageArea', value || 0)}
                                        placeholder="0,00"
                                        className={formErrors.storageArea ? 'border-destructive' : ''}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="freeArea" className="text-xs text-muted-foreground">Libre</Label>
                                    <UnitInput
                                        id="freeArea"
                                        unit="m²"
                                        value={formData.freeArea || undefined}
                                        onValueChange={(value) => handleInputChange('freeArea', value || 0)}
                                        placeholder="0,00"
                                        className={formErrors.freeArea ? 'border-destructive' : ''}
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end pt-2">
                                <div className="text-right">
                                    <span className="text-sm text-muted-foreground">Total: </span>
                                    <span className="text-lg font-bold text-emerald-600">{calculateTotalSurface().toFixed(2)} m²</span>
                                    {formErrors.totalSurface && <p className="text-xs text-destructive">{formErrors.totalSurface}</p>}
                                </div>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="notes" className="text-foreground">Notas</Label>
                            <Textarea
                                id="notes"
                                value={formData.notes}
                                onChange={(e) => handleInputChange('notes', e.target.value)}
                                maxLength={255}
                                className={`bg-background border-input text-foreground ${formErrors.notes ? 'border-destructive' : ''}`}
                                placeholder="Observaciones adicionales..."
                                size="sm"
                            />
                            <CharCounter value={formData.notes} max={255} />
                            {formErrors.notes && <p className="text-xs text-destructive">{formErrors.notes}</p>}
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 mt-4">
                        <Button variant="outline" onClick={() => setIsDialogOpen(false)} className="border-input hover:bg-accent text-foreground">
                            Cancelar
                        </Button>
                        <Button onClick={handleSave} className="text-white bg-indigo-600 hover:bg-indigo-700">
                            {editingUnit ? 'Actualizar' : 'Guardar Unidad'}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog >

            {/* Delete Confirmation Dialog */}
            < Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen} >
                <DialogContent className="rounded-lg bg-card border-border">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-foreground">
                            <AlertCircle className="w-5 h-5 text-destructive" />
                            Confirmar Eliminación
                        </DialogTitle>
                        <DialogDescription className="text-muted-foreground">
                            ¿Estás seguro de que deseas eliminar la unidad funcional <span className="font-medium text-foreground">{unitToDelete?.floor} - {unitToDelete?.letter}</span>?
                            Esta acción no se puede deshacer.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex justify-end gap-3 pt-4">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setIsDeleteDialogOpen(false);
                                setUnitToDelete(null);
                            }}
                            className="rounded-lg"
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={confirmDelete}
                            className="rounded-lg bg-destructive text-destructive-foreground hover:opacity-90"
                        >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Eliminar
                        </Button>
                    </div>
                </DialogContent>
            </Dialog >

            {/* Notes Modal */}
            <NotesModal
                open={isNotesModalOpen}
                onOpenChange={setIsNotesModalOpen}
                title={selectedUnitNotes ? `Notas - Piso ${selectedUnitNotes.floor} - ${selectedUnitNotes.letter}` : 'Notas'}
                notes={selectedUnitNotes?.notes || ''}
            />
        </div >
    );
};

export default ProjectFunctionalUnits;
